import React, { useEffect, useRef, useState } from "react"
import { Card, Input, Button, Avatar, Space, Flex } from "antd"
import { CloseOutlined, SendOutlined, ExpandOutlined, ShrinkOutlined } from "@ant-design/icons"
import { useSelector, useDispatch } from "react-redux"
import { closeChatCard } from "../../../store/aiassistant"
import ai from "assets/icons/ai.svg"
import styles from "./ChatBox.module.css"
import { Text } from "ui/Typography"
import axios from "axios"
import SystemPrompt from "./SystemPrompt"
import { spacings } from "themes/variables"
import { renderAIMessage } from "./utils"
import { useTranslation } from "react-i18next"

const ThinkingDots = () => (
  <span className={styles.thinkingDots}>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
  </span>
)

const ChatBox = () => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  const cardRef = useRef(null)
  const dispatch = useDispatch()
  const isOpen = useSelector(({ chatCard }) => chatCard.isOpen)
  const [fullscreen, setFullscreen] = useState(false)
  const [chatHistory, setChatHistory] = useState([])
  const [sessionId, setSessionId] = useState("")
  const [message, setMessage] = useState("")
  const [loading, setLoading] = useState(false)
  const accessToken = localStorage.getItem("access_token")
  const scrollAnchorRef = useRef(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "auto" })
    }
  }, [chatHistory, loading, isMainMenuOpen])

  useEffect(() => {
    if (isOpen) {
      const id = `s_${Math.floor(Math.random() * 100000)}`
      setSessionId(id)
      setChatHistory([])
    } else {
      setSessionId("")
      setChatHistory([])
    }
  }, [isOpen])

  const handleFullscreen = () => setFullscreen(true)
  const handleShrink = () => setFullscreen(false)

  const handleSendMessage = async () => {
    if (!message.trim()) return
    setChatHistory((prev) => [...prev, { id: Date.now() + Math.random(), role: "user", content: message }])
    const payload = {
      session_id: sessionId,
      query: message
    }
    setMessage("")
    setLoading(true)
    try {
      const { data } = await axios.post(process.env.REACT_APP_AI_ASSISTANT_URL, payload, {
        headers: {
          "DS-Api-Token": accessToken,
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`
        }
      })
      if (data?.details) {
        setChatHistory((prev) => [...prev, { id: Date.now() + Math.random(), role: "ai", content: data.details }])
      }
    } catch {
      setChatHistory((prev) => [
        ...prev,
        { id: Date.now() + Math.random(), role: "ai", content: "Sorry, there was an error. Please try again. " }
      ])
    } finally {
      setLoading(false)
    }
  }

  const handlePromptClick = (promptTitle) => {
    setMessage(promptTitle)
    setIsMainMenuOpen(false)
  }

  const handleClose = () => {
    dispatch(closeChatCard())
    setChatHistory([])
    setMessage("")
    setLoading(false)
  }

  return (
    isOpen && (
      <Card
        title={
          <Flex className={styles.headerSpace}>
            <Flex justify="center" align="center" gap={spacings.S} className={styles.headerTitle}>
              <Avatar src={ai} size={32} className={styles.headerAvatar} />
              <Text fw="500">{t("ai_assistant.ds_ai_assistant")}</Text>
            </Flex>
            <Space>
              {!fullscreen ? (
                <Button type="text" icon={<ExpandOutlined />} style={{ fontSize: 16 }} onClick={handleFullscreen} />
              ) : (
                <Button type="text" icon={<ShrinkOutlined />} style={{ fontSize: 16 }} onClick={handleShrink} />
              )}
              <Button type="text" icon={<CloseOutlined />} style={{ fontSize: 16 }} onClick={handleClose} />
            </Space>
          </Flex>
        }
        className={fullscreen ? `${styles.chatCard} ${styles.fullscreen}` : styles.chatCard}
        styles={{
          body: {
            padding: 0,
            height: "100%"
          }
        }}
        ref={cardRef}
      >
        <Flex vertical gap={spacings.M}>
          <SystemPrompt handlePromptClick={handlePromptClick} />
          <Flex vertical gap={spacings.L}>
            {chatHistory.map((msg) =>
              msg.role === "user" ? (
                <Flex key={msg.id} justify="end" className={`mr-s ${styles.userMessage}`}>
                  <Text className={styles.aiMessage}>{msg.content}</Text>
                </Flex>
              ) : (
                <Flex gap={spacings.S} key={msg.id}>
                  <div>
                    <Avatar src={ai} size={32} className={styles.headerAvatar} />
                  </div>
                  <Text className={styles.aiMessage}>{renderAIMessage(msg)}</Text>
                </Flex>
              )
            )}
            {isMainMenuOpen && <SystemPrompt handlePromptClick={handlePromptClick} />}
            {loading && (
              <Flex gap={spacings.M} align="center">
                <Avatar src={ai} size={32} className={styles.headerAvatar} />
                <ThinkingDots />
              </Flex>
            )}
            <div ref={scrollAnchorRef} />
          </Flex>
        </Flex>
        {chatHistory.length > 1 && (
          <Button className={styles.mainMenuButton} onClick={() => setIsMainMenuOpen(true)}>
            {t("ai_assistant.main_menu")}
          </Button>
        )}
        <div className={styles.footer}>
          <Input
            placeholder={t("ai_assistant.type_a_message")}
            size="small"
            className={styles.inputBox}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onPressEnter={handleSendMessage}
          />
          <Button
            disabled={loading || !message.trim()}
            type="primary"
            icon={<SendOutlined />}
            className={styles.sendButton}
            onClick={handleSendMessage}
          />
        </div>
      </Card>
    )
  )
}

export default ChatBox
